import { Reference } from '@approvalmax/types';
import { DateTimeEditor, DropdownEditor, Field, NumberEditor, TextAreaEditor, TextEditor } from '@approvalmax/ui';
import { Flex } from '@approvalmax/ui/src/components';
import { constants, selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import {
    changeQbooksShippingAddress,
    changeQbooksShippingDate,
    changeQbooksShippingFromAddress,
    changeQbooksShippingMethod,
    changeQbooksShippingPrice,
    changeQbooksShippingTaxCode,
    changeQbooksTrackingNumber,
} from 'pages/requestForm/actions';
import { SectionContainer } from 'pages/requestForm/components/SectionContainer';
import { getRequest } from 'pages/requestForm/selectors/pageSelectors';
import { getQBooksContext } from 'pages/requestForm/selectors/qbooks';
import { getTaxCodeList } from 'pages/requestForm/selectors/qbooks/taxSelectors';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { messages } from './SalesInvoiceShippingSection.messages';

export const SalesInvoiceShippingSection = memo(() => {
    const dispatch = useDispatch();

    const request = useSelector<domain.QBooksInvoiceRequest>(getRequest);
    const company = useSelector((state) => selectors.company.findCompanyById(state, request.companyId));
    const qBooksContext = useSelector(getQBooksContext);
    const { details, currency } = request;
    const { shipping, shippingTaxCode, lineAmountType } = details;
    const {
        method: shipVia,
        date: shippingDate,
        fromAddress: shippingFromAddress,
        price: shippingPrice,
        priceGross: shippingPriceGross,
        address: shippingTo,
        trackingNumber,
    } = shipping;
    const isInitialShippingPresent = useRef(Object.values(shipping).some(Boolean));
    const isShippingEnabled =
        qBooksContext.salesFormOptionalFields?.shippingEnabled || isInitialShippingPresent.current;

    const isUSOrg = company?.integration?.countryCode === backend.OrganisationVersion.QBO_US;

    const taxRates = useMemo(
        () => getTaxCodeList(qBooksContext, details.date, domain.TaxApplicableOnType.Sales),
        [details.date, qBooksContext]
    );

    const handleShippingMethodChange = useCallback(
        (value: string) => {
            dispatch(changeQbooksShippingMethod(value));
        },
        [dispatch]
    );

    const handleShippingAddressChange = useCallback(
        (value: string) => {
            dispatch(changeQbooksShippingAddress(value));
        },
        [dispatch]
    );

    const handleTrackingNumberChange = useCallback(
        (value: string) => {
            dispatch(changeQbooksTrackingNumber(value));
        },
        [dispatch]
    );

    const handleShippingDateChange = useCallback(
        (value: string) => {
            dispatch(changeQbooksShippingDate(value));
        },
        [dispatch]
    );

    const handleShippingFromAddressChange = useCallback(
        (value: string) => dispatch(changeQbooksShippingFromAddress(value)),
        [dispatch]
    );

    const handleShippingPriceChange = useCallback(
        (value: number) => dispatch(changeQbooksShippingPrice(value)),
        [dispatch]
    );

    const handleShippingTaxCodeChange = useCallback(
        (value: Reference) => dispatch(changeQbooksShippingTaxCode(value)),
        [dispatch]
    );

    if (!isShippingEnabled) {
        return null;
    }

    return (
        <SectionContainer title={messages.sectionTitle}>
            <Flex container spacing='0 20'>
                <Flex size={6} block>
                    <Field title={messages.shippingToField}>
                        <TextAreaEditor
                            minHeight={4}
                            maxHeight={12}
                            maxLength={constants.qBooksConstants.SHIPPING_ADDRESS_MAX_LENGTH}
                            value={shippingTo}
                            onChange={handleShippingAddressChange}
                            placeholder={messages.shippingToPlaceholder}
                            changeOnBlur
                        />
                    </Field>
                </Flex>

                <Flex direction='column' size={6} spacing='8 0'>
                    <Field title={messages.shipViaField}>
                        <TextEditor
                            value={shipVia}
                            maxLength={constants.qBooksConstants.SHIP_VIA_MAX_LENGTH}
                            onChange={handleShippingMethodChange}
                            placeholder={messages.shipViaPlaceholder}
                            changeOnBlur
                        />
                    </Field>

                    <Field title={messages.trackingNumberField}>
                        <TextEditor
                            value={trackingNumber}
                            maxLength={constants.qBooksConstants.TRACKING_NUMBER_MAX_LENGTH}
                            onChange={handleTrackingNumberChange}
                            placeholder={messages.trackingNumberPlaceholder}
                            changeOnBlur
                        />
                    </Field>

                    <Field title={messages.shippingDateField}>
                        <DateTimeEditor
                            value={shippingDate}
                            onChange={handleShippingDateChange}
                            placeholder={messages.shippingDatePlaceholder}
                        />
                    </Field>
                </Flex>

                <Flex direction='column' size={6} spacing='8 0'>
                    <Field title={messages.shippingFromField}>
                        <TextEditor
                            value={shippingFromAddress}
                            maxLength={constants.qBooksConstants.SHIPPING_ADDRESS_MAX_LENGTH}
                            onChange={handleShippingFromAddressChange}
                            placeholder={messages.shippingFromPlaceholder}
                            changeOnBlur
                        />
                    </Field>

                    <Field title={messages.shippingPriceField({ currency })}>
                        <NumberEditor
                            value={
                                lineAmountType === domain.LineAmountType.TaxInclusive
                                    ? shippingPriceGross ?? null
                                    : shippingPrice ?? null
                            }
                            onChange={handleShippingPriceChange}
                            placeholder={messages.shippingPricePlaceholder}
                            precision={2}
                            changeOnBlur
                        />
                    </Field>

                    {!isUSOrg && (
                        <Field title={messages.shippingTaxCodeField}>
                            <PlainDataProvider items={taxRates}>
                                <DropdownEditor
                                    value={shippingTaxCode}
                                    onChange={handleShippingTaxCodeChange}
                                    placeholder={messages.shippingTaxCodePlaceholder}
                                />
                            </PlainDataProvider>
                        </Field>
                    )}
                </Flex>
            </Flex>
        </SectionContainer>
    );
});

SalesInvoiceShippingSection.displayName = 'SalesInvoiceShippingSection';
